import type { ReactElement, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

interface PageWithSidebarProps {
  /**
   * Route-specific left navigation. Pass an already-composed `<Sidebar />`
   * (or any other aside) — this helper only owns the flex container so
   * each page can decide what its sidebar looks like.
   */
  readonly sidebar: ReactNode;
  readonly children: ReactNode;
  /**
   * Optional override for the `<main>`'s aria-label. Defaults to the
   * generic shell label; set this when a route has a more specific
   * landmark name (e.g. "Patient record").
   */
  readonly mainAriaLabel?: string;
}

/**
 * Two-column page layout: sidebar + scrollable main. Pages compose this
 * inside the route element so the shell (`AppLayout`) stays free of any
 * per-route knowledge. Pages without a sidebar should render their own
 * `<main>` directly instead of using this helper.
 */
export function PageWithSidebar({
  sidebar,
  children,
  mainAriaLabel,
}: PageWithSidebarProps): ReactElement {
  const { t } = useTranslation();
  return (
    <div className="flex h-full">
      {sidebar}
      <main
        aria-label={mainAriaLabel ?? t('shell.mainContent.ariaLabel')}
        data-region="content"
        // `min-w-0 min-h-0` prevents this flex child from growing past
        // its share — without it, tall route content can push `<main>`
        // beyond the viewport and create over-scroll past the sidebar.
        className="flex-1 min-w-0 min-h-0 overflow-y-auto bg-background"
      >
        {children}
      </main>
    </div>
  );
}
